import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { doc, onSnapshot, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { userService } from '../services/user.service';
import { getDefaultProfileImage } from '../config/constants';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Función de login
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  };

  // Login con Google
  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Verificar si el usuario ya existe
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      
      // Si el usuario no existe, crear nuevo perfil
      if (!userDoc.exists()) {
        const username = generateUsername(result.user.displayName);
        await setDoc(doc(db, 'users', result.user.uid), {
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName,
          photoURL: result.user.photoURL,
          username: username,
          bio: '',
          createdAt: new Date()
        });
      }
      // Si el usuario existe, no sobrescribir sus datos
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error en logout:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        const userDocRef = doc(db, 'users', user.uid);
        const unsubscribeProfile = onSnapshot(userDocRef, (doc) => {
          if (doc.exists()) {
            setUserProfile(doc.data());
          }
        });
        
        setLoading(false);
        return () => unsubscribeProfile();
      } else {
        setUser(null);
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const updateProfile = async (profileData) => {
    if (!user) throw new Error('No user logged in');
    
    try {
      await userService.updateUserProfile(user.uid, {
        ...profileData,
        updatedAt: new Date()
      });
      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const { email, password, ...profileData } = userData;
      
      // Validar email
      if (!email || !email.includes('@')) {
        throw new Error('Email inválido');
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Crear perfil en Firestore con datos iniciales
      const initialProfile = {
        ...profileData,
        email,
        uid: userCredential.user.uid,
        createdAt: new Date(),
        updatedAt: new Date(),
        photoURL: getDefaultProfileImage(email)  // Asegurarse de importar esta función
      };

      await userService.createUserProfile(initialProfile);
      
      // Esperar a que el perfil se cree antes de continuar
      const userDoc = await userService.getUserProfile(userCredential.user.uid);
      if (!userDoc) {
        throw new Error('Error al crear el perfil');
      }

      return userCredential.user;
    } catch (error) {
      console.error('Error en registro:', error);
      throw error;
    }
  };

  const value = {
    user,
    userProfile,
    login,
    loginWithGoogle,
    logout,
    register,
    loading,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const AuthConsumer = AuthProvider.Consumer; 

// Función auxiliar para generar username
const generateUsername = (displayName) => {
  if (!displayName) return '';
  return displayName
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9]/g, '')
    + Math.floor(Math.random() * 1000);
}; 